/**
 * Color Constants for Light and Dark Mode
 * Based on the Ideal Long-Use Palette specification
 */

export const colors = {
  light: {
    // Background
    background: '#F5F5F5',        // Light Gray - Reduces glare versus white
    formFields: '#FAFAFA',        // Off White - Soft neutral container

    // Text
    text: '#1F2937',              // Dark Gray - Keeps readability without sharp contrast
    textSecondary: '#6B7280',     // Medium Gray - Subdued tone to reduce cognitive load
    placeholder: '#9CA3AF',       // Medium Gray

    // Input specific
    inputBackground: '#FFFFFF',   // White - Clear input background
    inputText: '#1F2937',         // Dark Gray - Clear input text
    inputPlaceholder: '#9CA3AF',  // Medium Gray - Visible placeholder
    inputBorder: '#D1D5DB',       // Gray 300 - Visible input border

    // Button specific — shadcn-like dark filled primary on light surfaces
    buttonBackground: '#0F172A',  // Slate 900 - filled primary background
    buttonText: '#F8FAFC',        // Slate 50 - light label on dark button
    buttonBorder: '#0F172A',      // Match filled primary
    buttonHover: '#1E293B',       // Slate 800 - hover

    // Dropdown specific
    dropdownBackground: '#FFFFFF',  // White - Dropdown background
    dropdownText: '#1F2937',        // Dark Gray - Dropdown text
    dropdownHover: '#F2F1E9',       // Light hover
    dropdownBorder: '#E5E7EB',      // Light border

    // Accent & Interactive
    accent: '#2563EB',            // Desaturated Blue - Draws attention without fatigue
    button: '#0F172A',            // Align button token with primary
    primary: '#0F172A',           // Dark filled primary (shadcn light)
    primaryHover: '#1E293B',      // Slate 800 - hover state

    // Feedback
    success: '#059669',           // Calm Green - Encourages focus and ease
    error: '#DC2626',             // Soft Red - Noticeable, not aggressive
    warning: '#F59E0B',           // Amber 500 - Warning state
    info: '#3B82F6',              // Blue 500 - Info state

    // Semantic Colors
    green: '#22C55E',             // Green 500 - Success/positive actions
    blue: '#3B82F6',              // Blue 500 - Info/primary actions
    red: '#EF4444',               // Red 500 - Danger/delete actions
    purple: '#A855F7',            // Purple 500 - Special/premium actions
    errorBg: '#FEF2F2',           // Red 50 - Error background

    // Borders & Dividers
    border: '#E5E7EB',
    divider: 'rgba(0, 0, 0, 0.06)',

    // Surfaces
    card: '#FFFFFF',
    sidebar: '#F9F9F9',
    header: '#F9F9F9',

    // Table
    tableHeader: '#D3D3D3',
    tableHeaderText: '#1F2937',
    tableText: '#1F2937',
    tableRow: '#FCFCFA',
    tableRowAlt: '#FFFFFF',
    tableRowHover: '#F2F1E9',
    tableRowSelected: '#E8E7DF',
    tableFooter: '#FFFFFF',
    tableFooterText: '#1F2937',
  },
  dark: {
    // Background - Lighter charcoal for comfortable viewing
    background: '#374151',        // Gray 600 - Much lighter, easier on eyes
    formFields: '#4B5563',        // Gray 500 - Clear form visibility

    // Text - Clear and comfortable
    text: '#F9FAFB',              // Gray 50 - Very soft white
    textSecondary: '#E5E7EB',     // Gray 200 - Highly visible secondary
    placeholder: '#9CA3AF',       // Gray 400 - Clear placeholders

    // Input specific
    inputBackground: '#1F2937',   // Gray 800 - Dark input background
    inputText: '#F9FAFB',         // Gray 50 - Clear input text
    inputPlaceholder: '#9CA3AF',  // Gray 400 - Visible placeholder
    inputBorder: '#4B5563',       // Gray 600 - Visible input border

    // Button specific — shadcn-like light filled primary on dark surfaces
    buttonBackground: '#F8FAFC',  // Slate 50 - filled primary background
    buttonText: '#0F172A',        // Slate 900 - dark label on light button
    buttonBorder: '#F8FAFC',      // Match filled primary
    buttonHover: '#E2E8F0',       // Slate 200 - hover

    // Dropdown specific
    dropdownBackground: '#1F2937',  // Gray 800 - Dropdown background
    dropdownText: '#F9FAFB',        // Gray 50 - Dropdown text
    dropdownHover: '#334155',       // Gray 700 - Hover
    dropdownBorder: '#6B7280',      // Gray 500 - Border

    // Accent & Interactive — light primary (shadcn dark), blue kept on accent
    accent: '#60A5FA',            // Blue 400 - Bright and inviting
    button: '#F8FAFC',            // Align button token with primary
    primary: '#F8FAFC',           // Light filled primary (shadcn dark)
    primaryHover: '#E2E8F0',      // Slate 200 - Clear hover state

    // Feedback - Clear and friendly
    success: '#34D399',           // Emerald 400 - Bright success
    error: '#F87171',             // Red 400 - Clear error indication
    warning: '#FBBF24',           // Amber 400 - Bright warning
    info: '#60A5FA',              // Blue 400 - Clear info

    // Semantic Colors - Vibrant and clear
    green: '#6EE7B7',             // Emerald 300 - Very bright success
    blue: '#93C5FD',              // Blue 300 - Excellent visibility
    red: '#FCA5A5',               // Red 300 - Soft but visible
    purple: '#C4B5FD',            // Violet 300 - Clear special
    errorBg: 'rgba(248, 113, 113, 0.15)', // Subtle red background

    // Borders & Dividers - Clear definition
    border: '#6B7280',            // Gray 500 - More visible borders
    divider: 'rgba(229, 231, 235, 0.15)',

    // Surfaces - Lighter with excellent contrast
    card: '#4B5563',              // Gray 500 - Much lighter cards
    sidebar: '#1F2937',           // Gray 800 - Darker sidebar for good contrast
    header: '#4B5563',            // Gray 500 - Matches card

    // Table
    tableHeader: '#1E293B',
    tableHeaderText: '#F9FAFB',
    tableText: '#F9FAFB',
    tableRow: '#0F172A',
    tableRowAlt: '#1E293B',
    tableRowHover: '#334155',
    tableRowSelected: '#1E40AF',
    tableFooter: '#1E293B',
    tableFooterText: '#F9FAFB',
  },
  blue: {
    // Background - Blue-tinted dark theme
    background: '#1E3A5F',        // Deep Blue - Professional and calming
    formFields: '#2C5282',        // Blue 700 - Clear form visibility

    // Text - Clear and comfortable
    text: '#F7FAFC',              // Gray 50 - Very soft white
    textSecondary: '#E2E8F0',     // Gray 200 - Highly visible secondary
    placeholder: '#A0AEC0',       // Gray 400 - Clear placeholders

    // Input specific
    inputBackground: '#1A365D',   // Blue 900 - Dark blue input background
    inputText: '#F7FAFC',         // Gray 50 - Clear input text
    inputPlaceholder: '#A0AEC0',  // Gray 400 - Visible placeholder
    inputBorder: '#2C5F8D',       // Blue 800 - Visible input border

    // Button specific
    buttonBackground: '#63B3ED',  // Primary - Luminous against deep surfaces
    buttonText: '#0B1120',        // Nearly black - Strong readability on bright button
    buttonBorder: '#4299E1',      // Blue 400 - Border definition
    buttonHover: '#4299E1',       // Blue 400 - Hover

    // Dropdown specific
    dropdownBackground: '#1A365D',  // Blue 900 - Dropdown background
    dropdownText: '#F7FAFC',        // Gray 50 - Dropdown text
    dropdownHover: '#2D4A73',       // Blue hover
    dropdownBorder: '#4A5568',      // Gray 600 - Border

    // Accent & Interactive - Bright blue accents
    accent: '#4299E1',            // Blue 400 - Bright and inviting
    button: '#63B3ED',            // Align button token with primary
    primary: '#63B3ED',           // Blue 300 - Very bright, excellent visibility
    primaryHover: '#4299E1',      // Blue 400 - Clear hover state

    // Feedback - Clear and friendly
    success: '#48BB78',           // Green 400 - Bright success
    error: '#FC8181',             // Red 400 - Clear error indication
    warning: '#F6AD55',           // Orange 400 - Bright warning
    info: '#4299E1',              // Blue 400 - Clear info

    // Semantic Colors - Vibrant and clear
    green: '#68D391',             // Green 300 - Very bright success
    blue: '#63B3ED',              // Blue 300 - Excellent visibility
    red: '#FEB2B2',               // Red 300 - Soft but visible
    purple: '#B794F4',            // Purple 300 - Clear special
    errorBg: 'rgba(252, 129, 129, 0.15)', // Subtle red background

    // Borders & Dividers - Clear definition
    border: '#4A5568',            // Gray 600 - More visible borders
    divider: 'rgba(226, 232, 240, 0.15)',

    // Surfaces - Blue-tinted surfaces
    card: '#2C5282',              // Blue 700 - Blue-tinted cards
    sidebar: '#1A365D',           // Blue 900 - Darker sidebar for good contrast
    header: '#2C5282',            // Blue 700 - Matches card

    // Table
    tableHeader: '#1A365D',
    tableHeaderText: '#F7FAFC',
    tableText: '#F7FAFC',
    tableRow: '#1E3A5F',
    tableRowAlt: '#2C5282',
    tableRowHover: '#2D4A73',
    tableRowSelected: '#4299E1',
    tableFooter: '#1A365D',
    tableFooterText: '#F7FAFC',
  },
  cream: {
    // Background
    background: '#FAF3E6',          // Cream
    formFields: '#FFFFFF',          // Pure white for max contrast
    inputBackground: '#FFFFFF',
  
    // Text
    text: '#3B2F1A',                // Deep brown (no change)
    inputText: '#3B2F1A',           // Deep brown (no change)
    placeholder: '#8C7C5B',         // Stronger taupe, more readable    // Pale Beige/Grey - Visible and gentle[web:5]
  
    // Input specific
    inputPlaceholder: '#B8AE99',  // Pale Beige/Grey - As in placeholder[web:5]
    inputBorder: '#C5A77D',       // Warm light border, subtle definition[web:5]
  
    // Button specific
    buttonBackground: '#C5A77D',  // Camel - Warm contrast for action[web:5]
    buttonText: '#FFF8ED',        // Creamy white for contrast[web:5]
    buttonBorder: '#C5A77D',      // Matches button color[web:5]
    buttonHover: '#B3966B',       // Slightly deeper accent for hover state[web:5]
  
    // Dropdown specific
    dropdownBackground: '#FFF8ED',  // Cream[web:5]
    dropdownText: '#3B2F1A',        // Deep Brown[web:5]
    dropdownHover: '#F7ECD8',       // Soft beige highlight[web:5]
    dropdownBorder: '#E8DAC9',      // Cream border[web:5]
  
    // Accent & Interactive
    accent: '#A4876E',            // Muted ochre - Highlights and links[web:5]
    button: '#C5A77D',            // Align button token with primary[web:5]
    primary: '#C5A77D',           // Main brand accent for actions[web:5]
    primaryHover: '#B3966B',      // Slightly deeper accent for hover[web:5]
  
    // Feedback
    success: '#73B187',           // Muted Green - Calm, organic[web:5]
    error: '#D67B6E',             // Soft Red - Warm and inviting[web:5]
    warning: '#FFD384',           // Light gold/amber - Gentle warning[web:5]
    info: '#A3C1D8',              // Powder Blue - Subtle info highlight[web:5]
  
    // Semantic Colors
    green: '#A0C97F',             // Light Olive Green - Success/positive[web:5]
    blue: '#A3C1D8',              // Powder Blue - Info/action[web:5]
    red: '#D67B6E',               // Soft Red[web:5]
    purple: '#B2A4D4',            // Muted Lavender - Premium/special[web:5]
    errorBg: '#FBEDEC',           // Light Red/Cream - Error background[web:5]
  
    // Borders & Dividers
    border: '#E8DAC9',            // Cream border accent[web:5]
    divider: 'rgba(174, 156, 123, 0.10)',  // Transparent soft brown[web:5]
  
    // Surfaces
    card: '#FFF8ED',              // Cream surface[web:5]
    sidebar: '#F7ECD8',           // Slight beige depth[web:5]
    header: '#FAF3E6',            // Cream for header[web:5]
  
    // Table
    tableHeader: '#E8DAC9',       // Cream header[web:5]
    tableHeaderText: '#3B2F1A',   // Deep brown for header text[web:5]
    tableText: '#3B2F1A',         // Deep brown for table text[web:5]
    tableRow: '#FDF6EB',          // Lightest cream[web:5]
    tableRowAlt: '#FFF8ED',       // Almost white cream[web:5]
    tableRowHover: '#F7ECD8',     // Slightly darker beige[web:5]
    tableRowSelected: '#E8E0CC',  // Defined cream for selection[web:5]
    tableFooter: '#FFF8ED',       // Almost white cream for footer[web:5]
    tableFooterText: '#3B2F1A',   // Deep brown for footer text[web:5]
  },  
  navy: {
    // Background - Deep navy for proper dark theme feel
    background: '#1B2A41',        // Deep navy - rich and easy on eyes
    formFields: '#243B56',        // Slightly lighter navy - form containers

    // Text - Light text on dark backgrounds
    text: '#E2E8F0',              // Slate 200 - crisp readability on navy
    textSecondary: '#94A3B8',     // Slate 400 - clear secondary text
    placeholder: '#64748B',       // Slate 500 - visible placeholders

    // Input specific
    inputBackground: '#0F1D32',   // Darkest navy - strong contrast input
    inputText: '#F1F5F9',         // Slate 100 - clear input text
    inputPlaceholder: '#64748B',  // Slate 500 - visible placeholder
    inputBorder: '#334155',       // Slate 700 - visible input border

    // Button specific
    buttonBackground: '#3B82F6',  // Blue 500 - vibrant accent button
    buttonText: '#FFFFFF',        // White - clear button text
    buttonBorder: '#3B82F6',      // Blue 500 - matching border
    buttonHover: '#2563EB',       // Blue 600 - clear hover state

    // Dropdown specific
    dropdownBackground: '#0F1D32',  // Darkest navy - dropdown background
    dropdownText: '#F1F5F9',        // Slate 100 - dropdown text
    dropdownHover: '#1B2A41',       // Deep navy - hover
    dropdownBorder: '#334155',      // Slate 700 - border

    // Accent & Interactive - Blue accents for contrast on navy
    accent: '#60A5FA',            // Blue 400 - inviting accent
    button: '#3B82F6',            // Blue 500 - primary actions
    primary: '#3B82F6',           // Blue 500 - primary actions
    primaryHover: '#2563EB',      // Blue 600 - hover state

    // Feedback
    success: '#34D399',           // Emerald 400 - bright success
    error: '#F87171',             // Red 400 - clear error
    warning: '#FBBF24',           // Amber 400 - bright warning
    info: '#60A5FA',              // Blue 400 - clear info

    // Semantic Colors
    green: '#6EE7B7',             // Emerald 300 - visible success
    blue: '#93C5FD',              // Blue 300 - visible info
    red: '#FCA5A5',               // Red 300 - visible danger
    purple: '#C4B5FD',            // Violet 300 - special
    errorBg: 'rgba(248, 113, 113, 0.12)', // Subtle red background

    // Borders & Dividers
    border: '#334155',            // Slate 700 - visible borders
    divider: 'rgba(148, 163, 184, 0.12)',

    // Surfaces
    card: '#243B56',              // Lighter navy - card surfaces
    sidebar: '#0F1D32',           // Darkest navy - sidebar
    header: '#152238',            // Dark navy - header

    // Table - proper dark alternating rows
    tableHeader: '#0F1D32',       // Darkest - clear header distinction
    tableHeaderText: '#E2E8F0',   // Slate 200 - readable header text
    tableText: '#E2E8F0',         // Slate 200 - readable on both row colors
    tableRow: '#1B2A41',          // Deep navy - base row
    tableRowAlt: '#213349',       // Slightly lighter - subtle alternation
    tableRowHover: '#2A3F5F',     // Brighter navy - clear hover
    tableRowSelected: '#1E3A5F',  // Blue-navy - selection highlight
    tableFooter: '#152238',       // Dark navy footer
    tableFooterText: '#CBD5E1',   // Slate 300 - readable footer
  },
  purple: {
    // Background - Purple-tinted dark theme for a modern, creative feel
    background: '#2E294E',        // Deep Purple - Reduces eye strain like dark mode
    formFields: '#4A406E',        // Purple 700 - Clear form visibility

    // Text - Clear and comfortable, with subtle purple tint for cohesion
    text: '#F4F3FF',              // Very soft lavender white
    textSecondary: '#E0DEF4',     // Light purple gray - Highly visible secondary
    placeholder: '#A5A3C4',       // Medium purple gray - Clear placeholders

    // Input specific
    inputBackground: '#1F1A38',   // Dark Purple - Dark input background
    inputText: '#F4F3FF',         // Soft white - Clear input text
    inputPlaceholder: '#A5A3C4',  // Medium purple gray - Visible placeholder
    inputBorder: '#4A406E',       // Purple 700 - Visible input border

    // Button specific
    buttonBackground: '#C4B5FD',  // Violet 300 - High-contrast button background
    buttonText: '#1A112F',        // Dark purple - Strong readability on bright button
    buttonBorder: '#A78BFA',      // Violet 400 - Visible border
    buttonHover: '#A78BFA',       // Violet 400 - Hover

    // Dropdown specific
    dropdownBackground: '#1F1A38',  // Dark Purple - Dropdown background
    dropdownText: '#F4F3FF',        // Soft white - Dropdown text
    dropdownHover: '#332D55',       // Purple hover
    dropdownBorder: '#6D667F',      // Purple gray - Border

    // Accent & Interactive - Vibrant purple accents
    accent: '#A78BFA',            // Violet 400 - Bright and inviting
    button: '#C4B5FD',            // Align button token with primary
    primary: '#C4B5FD',           // Violet 300 - Very bright, excellent visibility
    primaryHover: '#A78BFA',      // Violet 400 - Clear hover state

    // Feedback - Clear and friendly, adapted to purple theme
    success: '#34D399',           // Emerald 400 - Bright success
    error: '#F87171',             // Red 400 - Clear error indication
    warning: '#FBBF24',           // Amber 400 - Bright warning
    info: '#A78BFA',              // Violet 400 - Clear info

    // Semantic Colors - Vibrant and clear
    green: '#6EE7B7',             // Emerald 300 - Very bright success
    blue: '#93C5FD',              // Blue 300 - Excellent visibility
    red: '#FCA5A5',               // Red 300 - Soft but visible
    purple: '#C4B5FD',            // Violet 300 - Clear special
    errorBg: 'rgba(248, 113, 113, 0.15)', // Subtle red background

    // Borders & Dividers - Clear definition
    border: '#6D667F',            // Purple gray - More visible borders
    divider: 'rgba(228, 224, 244, 0.15)', // Subtle lavender divider

    // Surfaces - Purple-tinted surfaces
    card: '#4A406E',              // Purple 700 - Tinted cards
    sidebar: '#1F1A38',           // Dark Purple - Darker sidebar for good contrast
    header: '#4A406E',            // Purple 700 - Matches card

    // Table
    tableHeader: '#1F1A38',
    tableHeaderText: '#F4F3FF',
    tableText: '#F4F3FF',
    tableRow: '#2E294E',
    tableRowAlt: '#3D3661',
    tableRowHover: '#4A406E',
    tableRowSelected: '#7C3AED',
    tableFooter: '#1F1A38',
    tableFooterText: '#F4F3FF',
  },
  green: {
    // Background - Soft sage green, lighter than before for eye comfort
    background: '#2E5043',        // Muted sage green - lifted from deep green
    formFields: '#3F6957',        // Lighter sage - Clear form visibility

    // Text - Clear and comfortable, with subtle green tint for cohesion
    text: '#F4FBF7',              // Very soft mint white
    textSecondary: '#D6E8DC',     // Light green gray - Highly visible secondary
    placeholder: '#9FC2AC',       // Medium green gray - Clear placeholders

    // Input specific
    inputBackground: '#264237',   // Soft dark sage - input background
    inputText: '#F4FBF7',         // Soft white - Clear input text
    inputPlaceholder: '#9FC2AC',  // Medium green gray - Visible placeholder
    inputBorder: '#4F8068',       // Sage border - Visible input border

    // Button specific
    buttonBackground: '#A7F3D0',  // Emerald 200 - High-contrast button background
    buttonText: '#11392A',        // Dark green - Strong readability on bright button
    buttonBorder: '#6EE7B7',      // Emerald 300 - Visible border
    buttonHover: '#6EE7B7',       // Emerald 300 - Hover

    // Dropdown specific
    dropdownBackground: '#264237',  // Soft dark sage - Dropdown background
    dropdownText: '#F4FBF7',        // Soft white - Dropdown text
    dropdownHover: '#37614F',       // Sage hover
    dropdownBorder: '#5E8B72',      // Green gray - Border

    // Accent & Interactive - Vibrant green accents
    accent: '#6EE7B7',            // Emerald 300 - Bright and inviting
    button: '#A7F3D0',            // Align button token with primary
    primary: '#A7F3D0',           // Emerald 200 - Very bright, excellent visibility
    primaryHover: '#6EE7B7',      // Emerald 300 - Clear hover state

    // Feedback - Clear and friendly, adapted to green theme
    success: '#A7F3D0',           // Emerald 200 - Bright success
    error: '#F87171',             // Red 400 - Clear error indication
    warning: '#FBBF24',           // Amber 400 - Bright warning
    info: '#6EE7B7',              // Emerald 300 - Clear info

    // Semantic Colors - Vibrant and clear
    green: '#A7F3D0',             // Emerald 200 - Very bright success
    blue: '#93C5FD',              // Blue 300 - Excellent visibility
    red: '#FCA5A5',               // Red 300 - Soft but visible
    purple: '#C4B5FD',            // Violet 300 - Clear special
    errorBg: 'rgba(248, 113, 113, 0.15)', // Subtle red background

    // Borders & Dividers - Clear definition
    border: '#5E8B72',            // Green gray - More visible borders
    divider: 'rgba(214, 232, 220, 0.15)', // Subtle mint divider

    // Surfaces - Green-tinted surfaces
    card: '#37614F',              // Sage - Tinted cards
    sidebar: '#21392F',           // Deep sage - Darker sidebar for contrast
    header: '#37614F',            // Sage - Matches card

    // Table
    tableHeader: '#21392F',
    tableHeaderText: '#F4FBF7',
    tableText: '#F4FBF7',
    tableRow: '#2E5043',
    tableRowAlt: '#37614F',
    tableRowHover: '#43745F',
    tableRowSelected: '#10B981',
    tableFooter: '#21392F',
    tableFooterText: '#F4FBF7',
  },
  red: {
    // Background - Red-tinted dark theme for a warm, energetic feel
    background: '#3A1F1F',        // Deep Red - Reduces eye strain like dark mode
    formFields: '#6B3D3D',        // Red 700 - Clear form visibility

    // Text - Clear and comfortable, with subtle red tint for cohesion
    text: '#FBF4F4',              // Very soft rose white
    textSecondary: '#EFDDE0',     // Light red gray - Highly visible secondary
    placeholder: '#C4A3A5',       // Medium red gray - Clear placeholders

    // Input specific
    inputBackground: '#2F1818',   // Dark Red - Dark input background
    inputText: '#FBF4F4',         // Soft white - Clear input text
    inputPlaceholder: '#C4A3A5',  // Medium red gray - Visible placeholder
    inputBorder: '#6B3D3D',       // Red 700 - Visible input border

    // Button specific
    buttonBackground: '#FCA5A5',  // Red 300 - High-contrast button background
    buttonText: '#200B0B',        // Dark red - Strong readability on bright button
    buttonBorder: '#F87171',      // Red 400 - Visible border
    buttonHover: '#F87171',       // Red 400 - Hover

    // Dropdown specific
    dropdownBackground: '#2F1818',  // Dark Red - Dropdown background
    dropdownText: '#FBF4F4',        // Soft white - Dropdown text
    dropdownHover: '#4D2E2E',       // Red hover
    dropdownBorder: '#7F5E5E',      // Red gray - Border

    // Accent & Interactive - Vibrant red accents
    accent: '#F87171',            // Red 400 - Bright and inviting
    button: '#FCA5A5',            // Align button token with primary
    primary: '#FCA5A5',           // Red 300 - Very bright, excellent visibility
    primaryHover: '#F87171',      // Red 400 - Clear hover state

    // Feedback - Clear and friendly, adapted to red theme
    success: '#34D399',           // Emerald 400 - Bright success
    error: '#FCA5A5',             // Red 300 - Clear error indication
    warning: '#FBBF24',           // Amber 400 - Bright warning
    info: '#93C5FD',              // Blue 300 - Clear info

    // Semantic Colors - Vibrant and clear
    green: '#6EE7B7',             // Emerald 300 - Very bright success
    blue: '#93C5FD',              // Blue 300 - Excellent visibility
    red: '#FCA5A5',               // Red 300 - Soft but visible
    purple: '#C4B5FD',            // Violet 300 - Clear special
    errorBg: 'rgba(252, 165, 165, 0.15)', // Subtle red background

    // Borders & Dividers - Clear definition
    border: '#7F5E5E',            // Red gray - More visible borders
    divider: 'rgba(239, 221, 224, 0.15)', // Subtle rose divider

    // Surfaces - Red-tinted surfaces
    card: '#6B3D3D',              // Red 700 - Tinted cards
    sidebar: '#2F1818',           // Dark Red - Darker sidebar for good contrast
    header: '#6B3D3D',            // Red 700 - Matches card

    // Table
    tableHeader: '#2F1818',
    tableHeaderText: '#FBF4F4',
    tableText: '#FBF4F4',
    tableRow: '#3A1F1F',
    tableRowAlt: '#4D2E2E',
    tableRowHover: '#6B3D3D',
    tableRowSelected: '#EF4444',
    tableFooter: '#2F1818',
    tableFooterText: '#FBF4F4',
  },

  orange: {
    // Background - Orange-tinted dark theme for a warm, inviting feel
    background: '#3A2E1F',        // Deep Orange - Reduces eye strain like dark mode
    formFields: '#6B4F3D',        // Orange 700 - Clear form visibility

    // Text - Clear and comfortable, with subtle orange tint for cohesion
    text: '#FBF7F4',              // Very soft peach white
    textSecondary: '#EFE0DD',     // Light orange gray - Highly visible secondary
    placeholder: '#C4A5A3',       // Medium orange gray - Clear placeholders

    // Input specific
    inputBackground: '#2F2018',   // Dark Orange - Dark input background
    inputText: '#FBF7F4',         // Soft white - Clear input text
    inputPlaceholder: '#C4A5A3',  // Medium orange gray - Visible placeholder
    inputBorder: '#6B4F3D',       // Orange 700 - Visible input border

    // Button specific
    buttonBackground: '#FDBA74',  // Orange 300 - High-contrast button background
    buttonText: '#20100B',        // Dark orange - Strong readability on bright button
    buttonBorder: '#FB923C',      // Orange 400 - Visible border
    buttonHover: '#FB923C',       // Orange 400 - Hover

    // Dropdown specific
    dropdownBackground: '#2F2018',  // Dark Orange - Dropdown background
    dropdownText: '#FBF7F4',        // Soft white - Dropdown text
    dropdownHover: '#4D382E',       // Orange hover
    dropdownBorder: '#7F655E',      // Orange gray - Border

    // Accent & Interactive - Vibrant orange accents
    accent: '#FB923C',            // Orange 400 - Bright and inviting
    button: '#FDBA74',            // Align button token with primary
    primary: '#FDBA74',           // Orange 300 - Very bright, excellent visibility
    primaryHover: '#FB923C',      // Orange 400 - Clear hover state

    // Feedback - Clear and friendly, adapted to orange theme
    success: '#34D399',           // Emerald 400 - Bright success
    error: '#F87171',             // Red 400 - Clear error indication
    warning: '#FDBA74',           // Orange 300 - Bright warning
    info: '#93C5FD',              // Blue 300 - Clear info

    // Semantic Colors - Vibrant and clear
    green: '#6EE7B7',             // Emerald 300 - Very bright success
    blue: '#93C5FD',              // Blue 300 - Excellent visibility
    red: '#FCA5A5',               // Red 300 - Soft but visible
    purple: '#C4B5FD',            // Violet 300 - Clear special
    errorBg: 'rgba(253, 186, 116, 0.15)', // Subtle orange background

    // Borders & Dividers - Clear definition
    border: '#7F655E',            // Orange gray - More visible borders
    divider: 'rgba(239, 224, 221, 0.15)', // Subtle peach divider

    // Surfaces - Orange-tinted surfaces
    card: '#6B4F3D',              // Orange 700 - Tinted cards
    sidebar: '#2F2018',           // Dark Orange - Darker sidebar for good contrast
    header: '#6B4F3D',            // Orange 700 - Matches card

    // Table
    tableHeader: '#2F2018',
    tableHeaderText: '#FBF7F4',
    tableText: '#FBF7F4',
    tableRow: '#3A2E1F',
    tableRowAlt: '#4D382E',
    tableRowHover: '#6B4F3D',
    tableRowSelected: '#F97316',
    tableFooter: '#2F2018',
    tableFooterText: '#FBF7F4',
  },

  pink: {
    // Background - Light pastel pink theme
    background: '#FDF2F8',        // Pink 50 - Soft pastel pink background
    formFields: '#FFFFFF',        // White - Clean form fields

    // Text - Dark for readability on light pink surfaces
    text: '#4A1942',              // Deep plum - Strong readability
    textSecondary: '#831843',     // Pink 900 - Darker secondary for better contrast
    placeholder: '#BE185D',       // Pink 700 - Visible placeholders

    // Input specific
    inputBackground: '#FFFFFF',   // White - Clear input background
    inputText: '#4A1942',         // Deep plum - Clear input text
    inputPlaceholder: '#C9A0BD',  // Light mauve - Visible placeholder
    inputBorder: '#F9A8D4',       // Pink 300 - Visible pink border

    // Button specific
    buttonBackground: '#DB2777',  // Pink 600 - Vibrant pink button
    buttonText: '#FFFFFF',        // White - Clear button text
    buttonBorder: '#DB2777',      // Pink 600 - Matching border
    buttonHover: '#BE185D',       // Pink 700 - Deeper hover

    // Dropdown specific
    dropdownBackground: '#FFFFFF',  // White - Clean dropdown
    dropdownText: '#4A1942',        // Deep plum - Readable text
    dropdownHover: '#FCE7F3',       // Pink 100 - Soft hover
    dropdownBorder: '#F9A8D4',      // Pink 300 - Visible border

    // Accent & Interactive - Vibrant pink accents
    accent: '#DB2777',            // Pink 600 - Strong accent for icons
    button: '#DB2777',            // Pink 600 - Button color
    primary: '#DB2777',           // Pink 600 - Primary actions
    primaryHover: '#BE185D',      // Pink 700 - Hover state

    // Feedback - Clear and friendly
    success: '#059669',           // Emerald 600 - Clear success
    error: '#DC2626',             // Red 600 - Clear error
    warning: '#D97706',           // Amber 600 - Warning
    info: '#2563EB',              // Blue 600 - Info

    // Semantic Colors
    green: '#16A34A',             // Green 600 - Visible success
    blue: '#2563EB',              // Blue 600 - Visible info
    red: '#DC2626',               // Red 600 - Visible danger
    purple: '#9333EA',            // Purple 600 - Visible special
    errorBg: '#FEF2F2',          // Red 50 - Error background

    // Borders & Dividers
    border: '#F9A8D4',            // Pink 300 - Visible pink borders
    divider: 'rgba(219, 39, 119, 0.12)', // Pink divider

    // Surfaces - Light pink surfaces
    card: '#FFFFFF',              // White - Clean cards
    sidebar: '#FCE7F3',           // Pink 100 - Distinguishable sidebar
    header: '#FBCFE8',            // Pink 200 - Visible header

    // Table
    tableHeader: '#FBCFE8',       // Pink 200 - Soft pink header
    tableHeaderText: '#4A1942',   // Deep plum - Readable header text
    tableText: '#4A1942',         // Deep plum - Readable table text
    tableRow: '#FFF1F9',          // Very light pink - Row background
    tableRowAlt: '#FFFFFF',       // White - Alternating row
    tableRowHover: '#FCE7F3',     // Pink 100 - Hover state
    tableRowSelected: '#FBCFE8',  // Pink 200 - Selected state
    tableFooter: '#FFFFFF',       // White - Footer
    tableFooterText: '#4A1942',   // Deep plum - Footer text
  },

  jarvis: {
    // Background - Manus AI exact colors
    background: '#1e1e1f',        // Darker base background
    formFields: '#2f2f30',        // Input box background

    // Text - Brighter for readability
    text: '#ececec',              // Bright off-white - primary text
    textSecondary: '#a0a0a0',     // Lighter gray - secondary text
    placeholder: '#6b6b6e',       // Medium gray - placeholders

    // Input specific
    inputBackground: '#2f2f30',   // Input box background
    inputText: '#ececec',         // Bright off-white - input text
    inputPlaceholder: '#6b6b6e',  // Medium gray - visible placeholder
    inputBorder: '#4a4a4c',       // Brighter border for visibility

    // Button specific — shadcn-like white primary on dark surfaces
    buttonBackground: '#FAFAFA',  // Near-white filled primary
    buttonText: '#18181B',        // Zinc 950 - dark text on white button
    buttonBorder: '#FAFAFA',      // Match filled primary
    buttonHover: '#E4E4E7',       // Zinc 200 - soft gray hover

    // Dropdown specific
    dropdownBackground: '#1e1e1f',  // Sidebar dark - dropdown bg
    dropdownText: '#ececec',         // Bright off-white dropdown text
    dropdownHover: '#272728',       // Active/hover state
    dropdownBorder: '#343435',      // Outer border gray

    // Accent & Interactive — white primary (shadcn dark), blue kept on accent/info
    accent: '#1988fe',            // Primary Blue (links / info accents)
    button: '#FAFAFA',            // Align button token with primary
    primary: '#FAFAFA',           // Near-white primary actions
    primaryHover: '#E4E4E7',      // Soft gray hover

    // Feedback
    success: '#34D399',           // Emerald 400 - Success
    error: '#F87171',             // Red 400 - Error
    warning: '#FBBF24',           // Amber 400 - Warning
    info: '#1988fe',              // Primary Blue - Info

    // Semantic Colors
    green: '#6EE7B7',             // Emerald 300 - Success
    blue: '#1988fe',              // Primary Blue
    red: '#FCA5A5',               // Red 300 - Danger
    purple: '#e9386f',            // Secondary Accent - pink/magenta
    errorBg: 'rgba(248, 113, 113, 0.15)', // Subtle red background

    // Borders & Dividers
    border: '#3c3c3d',            // Grid lines / borders - more visible
    divider: 'rgba(60, 60, 61, 0.5)', // Grid lines subtle

    // Surfaces
    card: '#2c2c2d',              // Card surface - visible against background
    sidebar: '#171718',           // Sidebar - darkest surface
    header: '#1e1e1f',            // Header bar - matches sidebar tone

    // Table - Manus table structure
    tableHeader: '#2c2c2d',       // Table header background
    tableHeaderText: '#ececec',    // Bright off-white header text
    tableText: '#ececec',          // Bright off-white body text
    tableRow: '#232324',          // Row background
    tableRowAlt: '#272728',       // Alternating row
    tableRowHover: '#2f2f30',     // Hover state
    tableRowSelected: '#272728',  // Active/hover state
    tableFooter: '#2c2c2d',       // Footer matches header
    tableFooterText: '#ececec',    // Bright off-white footer text
  },

  /**
   * Horizon — soft vertical ocean mist → sand.
   * Atmospheric pastel gradient (not Connect chartreuse→sky, not cream/terracotta).
   *
   * CSS variables (injected by ThemeContext):
   *   --jarvis-bg-gradient
   *   --jarvis-sidebar-gradient
   */
  horizon: {
    // Background — solid fallback + vertical mist→sand gradient
    background: '#E6EEEB',        // Cool mist midtone (fallback)
    backgroundGradient: 'linear-gradient(180deg, #C5D8D4 0%, #E4EBE8 48%, #EFE8DE 100%)',
    sidebarGradient: 'linear-gradient(180deg, #B9CEC9 0%, #D5E3DF 55%, #E8E0D4 100%)',
    formFields: '#F4F7F6',        // Soft pearl form containers

    // Text — deep sea-slate for contrast on pastels
    text: '#1A2E2B',              // Deep slate-teal
    textSecondary: '#5A6E6A',     // Muted sea-slate
    placeholder: '#8A9B97',       // Soft mist gray

    // Input specific
    inputBackground: '#FFFFFF',   // Crisp white inputs
    inputText: '#1A2E2B',
    inputPlaceholder: '#8A9B97',
    inputBorder: '#B8C9C4',       // Mist border

    // Button — deep sea teal primary (readable, trade-like)
    buttonBackground: '#1F3D38',
    buttonText: '#F4F7F6',
    buttonBorder: '#1F3D38',
    buttonHover: '#2A524B',

    // Dropdown
    dropdownBackground: '#FFFFFF',
    dropdownText: '#1A2E2B',
    dropdownHover: '#E4EBE8',
    dropdownBorder: '#C5D4D0',

    // Accent & Interactive
    accent: '#2A6B62',            // Teal accent (links / focus)
    button: '#1F3D38',
    primary: '#1F3D38',
    primaryHover: '#2A524B',

    // Feedback
    success: '#059669',
    error: '#DC2626',
    warning: '#D97706',
    info: '#0E7490',              // Cyan-teal info

    // Semantic
    green: '#10B981',
    blue: '#0E7490',
    red: '#EF4444',
    purple: '#64748B',            // Neutral slate (avoid purple cliché)
    errorBg: '#FEF2F2',

    // Borders & Dividers
    border: '#C5D4D0',
    divider: 'rgba(31, 61, 56, 0.08)',

    // Surfaces — cool pearl cards over atmospheric shell
    card: '#F7FAF9',
    sidebar: '#D5E3DF',           // Solid fallback for mist sidebar
    header: '#E4EBE8',

    // Table
    tableHeader: '#C5D4D0',
    tableHeaderText: '#1A2E2B',
    tableText: '#1A2E2B',
    tableRow: '#F4F7F6',
    tableRowAlt: '#FFFFFF',
    tableRowHover: '#E4EBE8',
    tableRowSelected: '#D5E3DF',
    tableFooter: '#F7FAF9',
    tableFooterText: '#1A2E2B',
  },

  /**
   * Horizon Night — dock lights on water.
   * Deep teal canvas, soft sand text, restrained coral/amber feedback.
   * No purple glow, no neon, no pure black.
   *
   * CSS variables (injected by ThemeContext):
   *   --jarvis-bg-gradient
   *   --jarvis-sidebar-gradient
   */
  'horizon-night': {
    // Background — deep dock water → slightly lifted teal
    background: '#0F1614',
    backgroundGradient: 'linear-gradient(180deg, #0F1614 0%, #141C19 48%, #1A221F 100%)',
    sidebarGradient: 'linear-gradient(180deg, #121A17 0%, #1A221F 55%, #1E2A27 100%)',
    formFields: '#1E2A27',

    // Text — soft sand / muted slate-green
    text: '#E8E4DC',
    textSecondary: '#8A9A95',
    placeholder: '#8A9A95',

    // Input specific
    inputBackground: '#1E2A27',
    inputText: '#E8E4DC',
    inputPlaceholder: '#8A9A95',
    inputBorder: '#2F3F3A',

    // Button — accent fill with sand label
    buttonBackground: '#3D8F84',
    buttonText: '#E8E4DC',
    buttonBorder: '#3D8F84',
    buttonHover: '#4A9E92',

    // Dropdown
    dropdownBackground: '#1E2A27',
    dropdownText: '#E8E4DC',
    dropdownHover: '#243330',
    dropdownBorder: '#2F3F3A',

    // Accent & Interactive
    // primary = text/icons on dark; accent = fills / progress / selected
    accent: '#3D8F84',
    button: '#3D8F84',
    primary: '#7EB8AE',
    primaryHover: '#93C7BE',

    // Feedback — soft coral / amber (restrained)
    success: '#5BA88A',
    error: '#D97B6C',
    warning: '#C9994A',
    info: '#7EB8AE',

    // Semantic
    green: '#5BA88A',
    blue: '#7EB8AE',
    red: '#D97B6C',
    purple: '#8A9A95',
    errorBg: 'rgba(217, 123, 108, 0.15)',

    // Borders & Dividers
    border: '#2F3F3A',
    divider: 'rgba(232, 228, 220, 0.08)',

    // Surfaces — card / raised
    card: '#1E2A27',
    sidebar: '#1A221F',
    header: '#1E2A27',

    // Table
    tableHeader: '#243330',
    tableHeaderText: '#E8E4DC',
    tableText: '#E8E4DC',
    tableRow: '#1E2A27',
    tableRowAlt: '#1A221F',
    tableRowHover: '#243330',
    tableRowSelected: '#243330',
    tableFooter: '#1E2A27',
    tableFooterText: '#E8E4DC',
  },

  /**
   * Horizon Dawn — sunrise over warm sand.
   * Warm-light companion to Horizon: soft sand → rose gradient, deep teal
   * primary retained for brand continuity, amber/terracotta accent.
   *
   * CSS variables (injected by ThemeContext):
   *   --jarvis-bg-gradient
   *   --jarvis-sidebar-gradient
   */
  dawn: {
    // Background — warm sand midtone (fallback) + vertical sand→rose gradient
    background: '#F2E9DB',
    backgroundGradient: 'linear-gradient(180deg, #EAD9BE 0%, #F1E6D2 48%, #F6EFE4 100%)',
    sidebarGradient: 'linear-gradient(180deg, #E0CBA8 0%, #EBDCC0 55%, #F2E8D8 100%)',
    formFields: '#F8F2E8',

    // Text — deep warm brown for contrast on warm pastels
    text: '#3B2F1F',
    textSecondary: '#6B5D49',
    placeholder: '#A79B85',

    // Input specific
    inputBackground: '#FFFFFF',
    inputText: '#3B2F1F',
    inputPlaceholder: '#A79B85',
    inputBorder: '#D8C9AF',

    // Button — deep sea teal primary (brand continuity with Horizon)
    buttonBackground: '#1F3D38',
    buttonText: '#F7F2E7',
    buttonBorder: '#1F3D38',
    buttonHover: '#2A524B',

    // Dropdown
    dropdownBackground: '#FFFFFF',
    dropdownText: '#3B2F1F',
    dropdownHover: '#F0E6D2',
    dropdownBorder: '#DCCDB2',

    // Accent & Interactive — amber/terracotta accent
    accent: '#B45309',
    button: '#1F3D38',
    primary: '#1F3D38',
    primaryHover: '#2A524B',

    // Feedback
    success: '#059669',
    error: '#DC2626',
    warning: '#D97706',
    info: '#0E7490',

    // Semantic
    green: '#10B981',
    blue: '#0E7490',
    red: '#EF4444',
    purple: '#8A5A2B',            // Warm muted umber (avoid purple cliché)
    errorBg: '#FEF2F2',

    // Borders & Dividers
    border: '#DCCDB2',
    divider: 'rgba(59, 47, 31, 0.08)',

    // Surfaces — warm pearl cards over sand
    card: '#FBF7EE',
    sidebar: '#EBDCC0',           // Solid fallback for sand sidebar
    header: '#F1E6D2',

    // Table
    tableHeader: '#DCCDB2',
    tableHeaderText: '#3B2F1F',
    tableText: '#3B2F1F',
    tableRow: '#F6EFE2',
    tableRowAlt: '#FFFFFF',
    tableRowHover: '#F0E6D2',
    tableRowSelected: '#E6D5B8',
    tableFooter: '#FBF7EE',
    tableFooterText: '#3B2F1F',
  },

  /**
   * Horizon Dusk — cool slate/indigo night.
   * Cooler, more neutral dark companion to Horizon Night: shifts the hue from
   * teal-green toward blue-indigo, with soft ice/slate text. Restrained
   * feedback, no neon, no pure black.
   *
   * CSS variables (injected by ThemeContext):
   *   --jarvis-bg-gradient
   *   --jarvis-sidebar-gradient
   */
  dusk: {
    // Background — deep slate → slightly lifted indigo
    background: '#0F1218',
    backgroundGradient: 'linear-gradient(180deg, #0F1218 0%, #141A24 48%, #1A2230 100%)',
    sidebarGradient: 'linear-gradient(180deg, #111722 0%, #1A2230 55%, #1E2838 100%)',
    formFields: '#1C2534',

    // Text — soft ice / slate white
    text: '#E2E6EE',
    textSecondary: '#8A94A8',
    placeholder: '#8A94A8',

    // Input specific
    inputBackground: '#1C2534',
    inputText: '#E2E6EE',
    inputPlaceholder: '#8A94A8',
    inputBorder: '#2C3849',

    // Button — soft indigo accent fill with ice label
    buttonBackground: '#5B7DD4',
    buttonText: '#E8ECF5',
    buttonBorder: '#5B7DD4',
    buttonHover: '#6C8BE0',

    // Dropdown
    dropdownBackground: '#1C2534',
    dropdownText: '#E2E6EE',
    dropdownHover: '#222D40',
    dropdownBorder: '#2C3849',

    // Accent & Interactive
    accent: '#5B7DD4',
    button: '#5B7DD4',
    primary: '#93A8E0',
    primaryHover: '#A6B8E8',

    // Feedback — soft coral / amber (restrained)
    success: '#5BA88A',
    error: '#D97B6C',
    warning: '#C9994A',
    info: '#7EA2E0',

    // Semantic
    green: '#5BA88A',
    blue: '#93A8E0',
    red: '#D97B6C',
    purple: '#9BA3B5',
    errorBg: 'rgba(217, 123, 108, 0.15)',

    // Borders & Dividers
    border: '#2C3849',
    divider: 'rgba(226, 230, 238, 0.08)',

    // Surfaces — slate cards over indigo shell
    card: '#1C2534',
    sidebar: '#161E2B',
    header: '#1C2534',

    // Table
    tableHeader: '#222D40',
    tableHeaderText: '#E2E6EE',
    tableText: '#E2E6EE',
    tableRow: '#1C2534',
    tableRowAlt: '#171F2C',
    tableRowHover: '#222D40',
    tableRowSelected: '#222D40',
    tableFooter: '#1C2534',
    tableFooterText: '#E2E6EE',
  },

  /**
   * Horizon Mist — neutral, low-saturation light shell.
   * Nearly monochrome: barely-there gray-green, minimal accent. Ideal for
   * marathon data-entry / review sessions. No gradient (keeps the shell calm).
   *
   * CSS variables (injected by ThemeContext): none (flat, no atmospheric shell).
   */
  mist: {
    // Background — soft neutral gray
    background: '#F3F4F6',        // Gray 100 - calm, glare-free
    formFields: '#FAFAFA',        // Off-white form containers

    // Text — near-black slate for readability
    text: '#1F2937',
    textSecondary: '#6B7280',
    placeholder: '#9CA3AF',

    // Input specific
    inputBackground: '#FFFFFF',
    inputText: '#1F2937',
    inputPlaceholder: '#9CA3AF',
    inputBorder: '#D1D5DB',

    // Button — neutral slate primary (consistent, low-glamour)
    buttonBackground: '#1F2937',
    buttonText: '#F9FAFB',
    buttonBorder: '#1F2937',
    buttonHover: '#374151',

    // Dropdown
    dropdownBackground: '#FFFFFF',
    dropdownText: '#1F2937',
    dropdownHover: '#F3F4F6',
    dropdownBorder: '#E5E7EB',

    // Accent & Interactive — muted gray-green, not teal
    accent: '#4B6E5E',
    button: '#1F2937',
    primary: '#1F2937',
    primaryHover: '#374151',

    // Feedback
    success: '#059669',
    error: '#DC2626',
    warning: '#D97706',
    info: '#3B82F6',

    // Semantic
    green: '#16A34A',
    blue: '#2563EB',
    red: '#EF4444',
    purple: '#6B7280',            // Neutral gray (avoid purple cliché)
    errorBg: '#FEF2F2',

    // Borders & Dividers
    border: '#E5E7EB',
    divider: 'rgba(31, 41, 55, 0.06)',

    // Surfaces — flat neutral
    card: '#FFFFFF',
    sidebar: '#EDEEF0',           // Soft gray sidebar
    header: '#F3F4F6',

    // Table
    tableHeader: '#E5E7EB',
    tableHeaderText: '#1F2937',
    tableText: '#1F2937',
    tableRow: '#FFFFFF',
    tableRowAlt: '#FAFAFA',
    tableRowHover: '#F3F4F6',
    tableRowSelected: '#E5E7EB',
    tableFooter: '#FFFFFF',
    tableFooterText: '#1F2937',
  },

  /**
   * APEX Intelligence — light.
   * Flat brand surfaces: cool mist base, brand-ink text, blue-600 CTA.
   * Tokens derived from the login page's --ls-* palette (login-showcase.css).
   */
  apex: {
    // Background — cool mist (login light base), flat, no gradient
    background: '#EEF2F6',
    formFields: '#FFFFFF',

    // Text — brand ink (lifted for light surfaces)
    text: '#0F2A44',
    textSecondary: '#5B6B7C',
    placeholder: '#8A9BAB',

    // Input specific
    inputBackground: '#FFFFFF',
    inputText: '#0F2A44',
    inputPlaceholder: '#8A9BAB',
    inputBorder: '#CBD5E1',

    // Button — APEX blue-600 CTA (same fill as the login button)
    buttonBackground: '#2563EB',
    buttonText: '#FFFFFF',
    buttonBorder: '#2563EB',
    buttonHover: '#1D4ED8',

    // Dropdown
    dropdownBackground: '#FFFFFF',
    dropdownText: '#0F2A44',
    dropdownHover: '#EFF6FF',
    dropdownBorder: '#DBE2EA',

    // Accent & Interactive
    accent: '#2563EB',
    button: '#2563EB',
    primary: '#2563EB',
    primaryHover: '#1D4ED8',

    // Feedback
    success: '#059669',
    error: '#DC2626',
    warning: '#D97706',
    info: '#2563EB',

    // Semantic
    green: '#10B981',
    blue: '#2563EB',
    red: '#EF4444',
    purple: '#7C3AED',
    errorBg: '#FEF2F2',

    // Borders & Dividers
    border: '#DBE2EA',
    divider: 'rgba(15, 42, 68, 0.08)',

    // Surfaces
    card: '#FFFFFF',
    sidebar: '#F4F7FA',
    header: '#F4F7FA',

    // Table
    tableHeader: '#DBE2EA',
    tableHeaderText: '#0F2A44',
    tableText: '#0F2A44',
    tableRow: '#F7FAFC',
    tableRowAlt: '#FFFFFF',
    tableRowHover: '#EFF6FF',
    tableRowSelected: '#DBEAFE',
    tableFooter: '#FFFFFF',
    tableFooterText: '#0F2A44',
  },

  /**
   * APEX Intelligence — night.
   * Brand-ink navy canvas (--ls-ink), slate text, blue-600 CTA.
   */
  'apex-night': {
    // Background — brand ink
    background: '#0B1C2B',
    formFields: '#102538',

    // Text
    text: '#EEF2F6',
    textSecondary: '#9FB0C0',
    placeholder: '#6B7F93',

    // Input specific
    inputBackground: '#102538',
    inputText: '#EEF2F6',
    inputPlaceholder: '#6B7F93',
    inputBorder: '#1D3D5C',

    // Button — APEX blue-600 CTA (login button fill, kept on dark)
    buttonBackground: '#2563EB',
    buttonText: '#FFFFFF',
    buttonBorder: '#2563EB',
    buttonHover: '#1D4ED8',

    // Dropdown
    dropdownBackground: '#102538',
    dropdownText: '#EEF2F6',
    dropdownHover: '#16304A',
    dropdownBorder: '#1D3D5C',

    // Accent & Interactive
    // primary = text/icons on dark; accent = fills / progress / selected
    accent: '#3B82F6',
    button: '#2563EB',
    primary: '#60A5FA',
    primaryHover: '#3B82F6',

    // Feedback
    success: '#34D399',
    error: '#F87171',
    warning: '#FBBF24',
    info: '#60A5FA',

    // Semantic
    green: '#4ADE80',
    blue: '#60A5FA',
    red: '#FB7185',
    purple: '#A78BFA',
    errorBg: 'rgba(248, 113, 113, 0.15)',

    // Borders & Dividers
    border: 'rgba(255, 255, 255, 0.08)',
    divider: 'rgba(238, 242, 246, 0.1)',

    // Surfaces
    card: '#102538',
    sidebar: '#081623',
    header: '#102538',

    // Table
    tableHeader: '#16304A',
    tableHeaderText: '#EEF2F6',
    tableText: '#EEF2F6',
    tableRow: '#0B1C2B',
    tableRowAlt: '#102538',
    tableRowHover: '#16304A',
    tableRowSelected: '#1D3D5C',
    tableFooter: '#102538',
    tableFooterText: '#EEF2F6',
  },
} as const;

export type ThemeMode =
  | 'light'
  | 'dark'
  | 'blue'
  | 'cream'
  | 'navy'
  | 'purple'
  | 'green'
  | 'red'
  | 'orange'
  | 'pink'
  | 'jarvis'
  | 'horizon'
  | 'horizon-night'
  | 'dawn'
  | 'dusk'
  | 'mist'
  | 'apex'
  | 'apex-night';

export const DARK_THEME_MODES: ThemeMode[] = [
  'dark',
  'blue',
  'navy',
  'purple',
  'green',
  'red',
  'orange',
  'pink',
  'jarvis',
  'horizon-night',
  'dusk',
  'apex-night',
];

/** Themes with light sidebar / shell surfaces (dark text on active pills). */
export const LIGHT_THEME_MODES: ThemeMode[] = ['light', 'cream', 'pink', 'horizon', 'dawn', 'mist', 'apex'];

/** Brand themes exposed in the theme picker (APEX first, then Horizon family). */
export const SELECTABLE_THEME_MODES: ThemeMode[] = ['apex', 'apex-night', 'horizon', 'horizon-night', 'dawn', 'dusk', 'mist'];

export const DEFAULT_THEME_MODE: ThemeMode = 'apex';
